import { create } from "zustand";

/**
 * i18n của dashboard bube-hq — HQ-2026-006.
 *
 * ── Từ điển CÓ KIỂU, không thư viện ─────────────────────────────────────────
 *
 * Mỗi trang một namespace (`APP_STRUCTURE.md`): `vi/<ns>.ts` là bản gốc, `en/<ns>.ts` khai kiểu `Dict<typeof vi>`.
 * Thiếu một khoá ở `en`, thừa một khoá, hay gọi `t("khoá-không-có")` đều là lỗi `tsc` — không phải chuỗi khoá lộ ra màn
 * hình lúc chạy. Hub chỉ có hai ngôn ngữ và không cần plural/ICU, nên không kéo `i18next` vào (đỡ một lần đụng lockfile
 * của cả monorepo).
 *
 * ── Ngôn ngữ chọn ở đâu ─────────────────────────────────────────────────────
 *
 * Nút VI/EN ở thanh đầu trang → `localStorage["hq-locale"]`. Chưa chọn thì theo `navigator.language`
 * (`vi*` ⇒ vi, còn lại ⇒ en). Đổi ngôn ngữ cập nhật `<html lang>`.
 *
 * Ngoài phạm vi (ghi ở plan): chữ do MÁY CHỦ trả (thông báo lỗi `HqError`, ghi chú danh mục đối tác) vẫn là tiếng Việt.
 */
export const LOCALES = ["vi", "en"] as const;
export type Locale = (typeof LOCALES)[number];

/** Bản dịch phải có ĐÚNG các khoá của bản gốc tiếng Việt. */
export type Dict<T> = { readonly [K in keyof T]: string };

const STORAGE_KEY = "hq-locale";

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "vi" || saved === "en") return saved;
  } catch {
    // localStorage bị chặn (cửa sổ riêng tư…) — theo trình duyệt.
  }
  if (typeof navigator === "undefined") return "vi";
  return navigator.language.toLowerCase().startsWith("vi") ? "vi" : "en";
}

type LocaleState = { locale: Locale; setLocale: (l: Locale) => void };

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: initialLocale(),
  setLocale: (locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Không lưu được thì vẫn đổi cho phiên này.
    }
    if (typeof document !== "undefined") document.documentElement.lang = locale;
    set({ locale });
  },
}));

export const useLocale = (): Locale => useLocaleStore((s) => s.locale);

/** Ngôn ngữ hiện tại ngoài React (formatter, hằng số…). Gọi trong render thì dùng `useLocale` để còn render lại. */
export const currentLocale = (): Locale => useLocaleStore.getState().locale;

/** Tag BCP-47 cho `Intl.*`. */
export const intlLocaleOf = (l: Locale): string => (l === "vi" ? "vi-VN" : "en-US");

/** `{name}` trong chuỗi ⇒ `vars.name`. Biến không có ⇒ giữ nguyên `{name}` (nhìn thấy được, không im lặng mất chữ). */
export function interpolate(template: string, vars?: Readonly<Record<string, string | number>>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}
